// SEPLA 3D hero — six-chapter story, profile generator and camera keyframes (pure logic, no three.js imports)

export const NB = 6;

export const BEATS = [
  {
    short: "Coil",
    name: "Steel coil",
    machine: "Mill coil → flat sheet",
    copy: "Food-grade stainless steel unrolls into a flat sheet, ready for precise cutting.",
  },
  {
    short: "Pressing",
    name: "Pressing",
    machine: "Blanking die · deep-draw press",
    copy: "A circular blank is cut, then pressed into a seamless pot shape.",
  },
  {
    short: "Shaping",
    name: "Shaping",
    machine: "Trimming · rim rolling · handle fitting",
    copy: "The rim is trimmed and rolled smooth, then strong side handles are attached.",
  },
  {
    short: "Polishing",
    name: "Polishing",
    machine: "Buffing and finishing",
    copy: "Hand-finished to a mirror shine that resists stains and corrosion.",
  },
  {
    short: "Quality",
    name: "Quality check",
    machine: "Laser scan · permanent marking",
    copy: "A laser scans every surface and permanently marks each piece as SEPLA.",
  },
  {
    short: "Range",
    name: "The range",
    machine: "Sepla product range",
    copy: "Built in steel. Built for scale.",
    cta: true,
  },
];

export const clamp = (x, a = 0, b = 1) => Math.max(a, Math.min(b, x));
export const sst = (a, b, x) => { const u = clamp((x - a) / (b - a)); return u * u * u * (u * (u * 6 - 15) + 10); };
export const lerp = (a, b, u) => a + (b - a) * u;

// Animation channels as functions of story time T (0..NB). Chapter i owns [i, i+1).
export function channels(T) {
  return {
    unroll: sst(0.12, 0.82, T),
    blank: sst(1.1, 1.38, T),
    sheetAway: sst(1.42, 1.82, T),
    draw: sst(1.45, 1.95, T),
    trim: sst(2.12, 2.48, T),
    curl: sst(2.34, 2.74, T),
    handles: sst(2.56, 2.96, T),
    polish: sst(3.14, 3.82, T),
    scan: sst(4.12, 4.52, T),
    laser: sst(4.42, 4.92, T),
    finale: sst(5.14, 5.92, T),
  };
}

// Camera keyframes, one per chapter. The stage sits right on desktop and centres on mobile.
export const CAM = [
  { dx: 0, y: 1.0, z: 7.2, lookY: 0.35 },
  { dx: 0.15, y: 1.45, z: 6.2, lookY: 0.48 },
  { dx: 0, y: 1.22, z: 5.35, lookY: 0.72 },
  { dx: 0.35, y: 1.28, z: 5.0, lookY: 0.72 },
  { dx: 0, y: 1.12, z: 4.65, lookY: 0.72 },
  { dx: 0, y: 1.5, z: 8.2, lookY: 0.55 },
];

// Fixed-point lathe profile: capsule base, outer wall, rolled rim, inner wall and floor.
export const NP = 32;
export function potProfile({ R, H, wall = 0.055, bead = 0, jag = 0 }) {
  const h = Math.max(H, 0.05);
  const baseY = Math.min(0.035, h * 0.2);
  const capH = Math.min(0.16, Math.max(0.035, h * 0.13));
  const rimY = Math.max(h, capH + 0.025);
  const pts = [];

  // underside and capsule-base foot (6)
  pts.push([0, baseY], [R * 0.42, baseY], [R * 0.76, baseY + 0.004]);
  pts.push([R * 0.88, baseY + capH * 0.2], [R * 0.95, baseY + capH * 0.58], [R * 0.98, baseY + capH]);

  // outer wall (8)
  for (let k = 0; k < 8; k++) {
    const t = (k + 1) / 8;
    const y = baseY + capH + (rimY - baseY - capH) * t;
    const r = R * (0.98 + 0.025 * t);
    const wave = jag > 0 && t > 0.68 ? jag * 0.035 * Math.sin(k * 2.4) : 0;
    pts.push([r, y + wave]);
  }

  // rolled rim (5)
  const rimR = R * 1.005;
  for (let k = 0; k < 5; k++) {
    const t = (k + 1) / 5;
    const curl = bead * Math.sin(t * Math.PI);
    pts.push([
      rimR + curl * 0.055 - wall * Math.max(0, t - 0.45),
      rimY + curl * 0.075 + (jag > 0 ? jag * 0.018 * Math.sin(k * 1.9) : 0),
    ]);
  }

  // inner wall (8)
  const floorY = Math.max(baseY + wall * 0.7, 0.045);
  for (let k = 0; k < 8; k++) {
    const t = (k + 1) / 8;
    const r = (rimR - wall) * (1 - 0.08 * t * t);
    const y = rimY + (floorY - rimY) * t;
    pts.push([r, y]);
  }

  // inner floor (5)
  pts.push([(rimR - wall) * 0.78, floorY], [(rimR - wall) * 0.58, floorY * 0.96]);
  pts.push([(rimR - wall) * 0.34, floorY * 0.92], [(rimR - wall) * 0.14, floorY * 0.9], [0, floorY * 0.88]);

  return pts; // 32 points
}
